import { useState } from 'react';
import api from '../lib/api';
import { Search, Ticket, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { HeaderLanding } from "../components/ui/header-landings";
import { Footer } from '../components/ui/footer';
import { toast } from 'sonner';

// Helper function to format ticket numbers with leading zeros
const formatTicketNumber = (num, totalTickets) => {
  const digits = String(totalTickets).length;
  return String(num).padStart(digits, '0');
};

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
 

  const handleSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 3) {
      toast.error('Ingresa al menos 3 caracteres para buscar');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await api.post('/search', { query });
      setResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al buscar');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
       {/* Header */}
      <HeaderLanding />

      <main className="px-4 sm:px-8 max-w-4xl mx-auto py-12">
        {/* Search Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3333] mb-2">
            Consulta tus boletos
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">
            BUSCAR BOLETOS
          </h1>
          <p className="text-[#4B5563] max-w-md mx-auto">
            Ingresa tu nombre, correo electrónico o número de teléfono para encontrar tus boletos apartados.
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-12">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre, correo o teléfono..."
                className="pl-12 border-2 border-black rounded-none h-14 text-lg focus:ring-0 focus:border-[#FF3333]"
                data-testid="search-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-[#FF3333] text-white font-bold uppercase tracking-wider border-2 border-black px-8 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all disabled:opacity-50"
              data-testid="search-submit"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-black border-t-transparent"></div>
          </div>
        ) : searched && results.length === 0 ? (
          <div className="bg-[#F3F4F6] border-2 border-black p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-[#F59E0B]" />
            <h3 className="text-2xl font-bold mb-2">No se encontraron boletos</h3>
            <p className="text-[#4B5563]">
              Verifica que los datos ingresados sean correctos o intenta con otra búsqueda.
            </p>
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-6" data-testid="search-results">
            <p className="text-sm text-[#4B5563]">
              Se encontraron <span className="font-bold text-black">{results.length}</span> resultado(s)
            </p>
            
            {results.map((result) => (
              <div 
                key={result.id}
                className="bg-white border-2 border-black shadow-[8px_8px_0_0_#000000] hover:-translate-y-1 hover:shadow-[12px_12px_0_0_#000000] transition-all"
                data-testid="search-result-card"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  {result.raffle_image && (
                    <div className="md:w-48 h-32 md:h-auto border-b-2 md:border-b-0 md:border-r-2 border-black">
                      <img 
                        src={result.raffle_image}
                        alt={result.raffle_title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold">{result.raffle_title}</h3>
                        <p className="text-sm text-[#4B5563]">{result.buyer_name}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-bold uppercase ${
                        result.status === 'confirmed' 
                          ? 'bg-[#D1FAE5] text-[#065F46]' 
                          : 'bg-[#FEF3C7] text-[#92400E]'
                      }`}>
                        {result.status === 'confirmed' ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Confirmado
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3" />
                            Pendiente
                          </>
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <Ticket className="w-5 h-5 text-[#FF3333]" />
                      <span className="font-bold">Boleto(s):</span>
                      <span className="font-mono bg-[#F3F4F6] px-3 py-1 border border-black">
                        {result.tickets.join(', ')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[#4B5563]">
                        Apartado: {new Date(result.created_at).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#F3F4F6] border-2 border-black p-12 text-center">
            <Search className="w-16 h-16 mx-auto mb-4 text-[#D1D5DB]" />
            <h3 className="text-2xl font-bold mb-2">Busca tus boletos</h3>
            <p className="text-[#4B5563]">
              Ingresa tus datos para encontrar tus boletos apartados.
            </p>
          </div>
        )}
      </main>

    
    {/* Footer */}
      <Footer />
    </div>
  );
}
